<template>
    <OpportunityPageDetailTopLayout class="mb-0 sm:pb-8" hide-line-break job-layout left-hidden-on-mobile variant="job">
        <template #image>
            <CThumbnailImage
                v-if="org"
                :alt="`Organizácia ${org.title} logo`"
                class="rounded-md text-gray-750 dark:text-txt-headings"
                :src="org.image"
                type="job"
            />
        </template>

        <template #highlights>
            <div v-if="job" class="flex flex-col">
                <CHeading class="mb-1" variant="1">
                    {{ job.title }}
                </CHeading>

                <LayoutTopOrgProfileLink v-if="org" :org="org" :preview="preview" />

                <IconTextRow v-if="job.highlights" icon="quote">
                    {{ job.highlights }}
                </IconTextRow>

                <!-- Key job parameters - visible immediately -->
                <div class="mb-4 flex flex-wrap gap-x-4 gap-y-2 text-gray-600 dark:text-txt-body">
                    <span v-if="jobPlaceText" class="flex items-center gap-1.5">
                        <CIcon name="location-outline" class="size-5" />
                        {{ jobPlaceText }}
                    </span>
                    <span v-if="jobTimeText" class="flex items-center gap-1.5">
                        <CIcon name="clock" class="size-5" />
                        {{ jobTimeText }}
                    </span>
                    <span v-if="jobTypeText" class="flex items-center gap-1.5">
                        <CIcon name="jobs" class="size-5" />
                        {{ jobTypeText }}
                    </span>
                    <span v-if="salaryText" class="flex items-center gap-1.5">
                        <span>€</span>
                        {{ salaryText }}
                    </span>
                    <span v-if="endDateText" class="flex items-center gap-1.5">
                        <CIcon name="calendar" class="size-5" />
                        {{ t('jobView.jobDetails.applicationsUntil', { date: endDateText }, lang) }}
                    </span>
                </div>

                <ProfileHighlightsInterests
                    v-if="job.topics && job.topics.length"
                    :highlighted="highlighted"
                    :interests="job.topics"
                    :preview="preview"
                    query-search-param="topics"
                    search-route-name="praca"
                    :tooltips="tooltips"
                />
            </div>
        </template>
    </OpportunityPageDetailTopLayout>
</template>

<script setup lang="ts">
import type { Job } from '#shared/types/Job'
import { JobPlaceType } from '#shared/types/Job'
import type { Org } from '#shared/types/Org'
import { formatDate } from '#shared/utils'
import { useJobs } from '@/composables/jobs/useJobs'
import CIcon from '@/components/ui/CIcon.vue'

interface Props {
    job?: Job
    org?: Org
    preview?: boolean
}

const props = defineProps<Props>()
const { t } = useI18n()
const { formatJobSalary } = useJobs()

const lang = computed(() => props.job?.lang || 'sk')

const highlighted = ref(['flexi'])
const tooltips = computed(() => ({
  flexi: t('jobForm.fields.flexi.info', lang.value)
}))

// Key job parameters computed properties
const jobPlaceText = computed(() => {
    if (!props.job?.jobPlace?.length) return ''
    const places = props.job.jobPlace.map(p => t(`jobView.jobPlace.type.${p}`, lang.value))
    const jobLocation = props.job.jobLocation?.join(', ')
    const placeWithLocation = props.job.jobPlace?.includes(JobPlaceType.office)
        ? JobPlaceType.office
        : JobPlaceType.hybrid
    // Show location only if office or hybrid is selected
    if (jobLocation && props.job.jobPlace?.includes(placeWithLocation)) {
        return `${places.join(' / ')} · ${jobLocation}`
    }
    return places.join(' / ')
})

const jobTimeText = computed(() => {
    if (!props.job?.jobTime?.length) return ''
    return props.job.jobTime.map(time => t(`jobView.jobTime.type.${time}`, lang.value)).join(', ')
})

const jobTypeText = computed(() => {
    if (!props.job?.type?.length) return ''
    return props.job.type.map(type => t(`jobForm.fields.type.values.${type}`, lang.value)).join(' / ')
})

// The row's labels follow the job's language, so the date does too
const endDateText = computed(() => {
    if (!props.job?.endDate) return ''
    const isEnglish = lang.value === 'en'
    return formatDate(props.job.endDate, {
        format: isEnglish ? 'D MMMM YYYY' : 'D. M. YYYY',
        locale: isEnglish ? 'en' : 'sk'
    })
})

const salaryText = computed(() => {
    if (!props.job) return ''
    return formatJobSalary(
        props.job.type || [],
        props.job.salaryMin || 0,
        props.job.salaryMax || 0,
        t('jobView.jobSalary.internship', lang.value).toString(),
        t('jobView.jobSalary.compensationNotSpecified', lang.value).toString(),
        t('jobView.jobSalary.prefixFrom', lang.value).toString(),
        t('jobView.jobSalary.prefixTo', lang.value).toString(),
        t(`jobView.jobSalary.unit.${props.job.salaryUnit}`, lang.value).toString()
    )
})
</script>
